export interface ActionSuggestion {
  type: 'execute_outcome' | 'run_workflow' | 'change_phase' | 'send_command';
  label: string;
  outcomeId?: string;
  workflowName?: string;
  phase?: number;
  command?: string;
}

interface PatternMatch {
  type: ActionSuggestion['type'];
  regex: RegExp;
  build: (match: RegExpExecArray) => ActionSuggestion;
}

const patterns: PatternMatch[] = [
  {
    type: 'execute_outcome',
    regex: /execute\s+(?:outcome\s+)?(O-[A-Z]{2,5}-\d{3})/gi,
    build: (match: RegExpExecArray): ActionSuggestion => ({
      type: 'execute_outcome',
      label: `Execute outcome ${match[1]}`,
      outcomeId: match[1],
    }),
  },
  {
    type: 'run_workflow',
    regex: /run\s+(?:the\s+)?(\S+)\s+workflow/gi,
    build: (match: RegExpExecArray): ActionSuggestion => ({
      type: 'run_workflow',
      label: `Run ${match[1]} workflow`,
      workflowName: match[1],
    }),
  },
  {
    type: 'change_phase',
    regex: /(?:advance|move|transition)\s+to\s+(?:phase\s+)?(\d+)/gi,
    build: (match: RegExpExecArray): ActionSuggestion => ({
      type: 'change_phase',
      label: `Advance to phase ${match[1]}`,
      phase: Number(match[1]),
    }),
  },
  {
    type: 'send_command',
    regex: /run\s+(\/\w+)/gi,
    build: (match: RegExpExecArray): ActionSuggestion => ({
      type: 'send_command',
      label: `Run ${match[1]}`,
      command: match[1],
    }),
  },
];

function deduplicationKey(action: ActionSuggestion): string {
  switch (action.type) {
    case 'execute_outcome':
      return `${action.type}:${action.outcomeId}`;
    case 'run_workflow':
      return `${action.type}:${action.workflowName}`;
    case 'change_phase':
      return `${action.type}:${action.phase}`;
    case 'send_command':
      return `${action.type}:${action.command}`;
  }
}

export function parseActionSuggestions(content: string): ActionSuggestion[] {
  const results: ActionSuggestion[] = [];
  const seen = new Set<string>();

  for (const pattern of patterns) {
    // Reset lastIndex for global regex reuse across calls
    pattern.regex.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.regex.exec(content)) !== null) {
      const action = pattern.build(match);
      const key = deduplicationKey(action);
      if (!seen.has(key)) {
        seen.add(key);
        results.push(action);
      }
    }
  }

  return results;
}
